import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowLeft, ChevronRight, ChefHat, AlertTriangle, Flame } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FAMILY_LABELS = {
  mother: "Mother Sauce",
  emulsion: "Emulsion",
  reduction: "Reduction",
  condiment: "Condiment",
  vinaigrette: "Vinaigrette",
  modern: "Modern",
};

const SauceDetail = () => {
  const { slug } = useParams();
  const [sauce, setSauce] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/sauces/${slug}`)
      .then((res) => { setSauce(res.data); setStatus("ready"); })
      .catch(() => setStatus("missing"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  if (status === "missing") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950 pt-24 px-6">
        <div className="text-center max-w-md">
          <div className="magazine-number text-9xl mb-6">Ø</div>
          <p className="font-serif-display italic text-2xl text-stone-400 mb-8">
            Sauce not found. Either it's broken (deglazed too hard) or it never existed.
          </p>
          <Link to="/sauces" className="text-orange-500 uppercase text-xs tracking-[0.24em] ink-link">
            Back to the sauce larder
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 pt-16 pb-32">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-12">
        <Link
          to="/sauces"
          data-testid="sauce-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link mb-12"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Sauce larder
        </Link>

        <div className="border-b border-stone-800 pb-14 mb-14">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <span className="eyebrow text-orange-500">{FAMILY_LABELS[sauce.family] || sauce.family}</span>
                <div className="w-16 h-px bg-orange-600" />
                <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
                  {sauce.origin}
                </span>
              </div>
              <h1
                data-testid="sauce-title"
                className="font-serif-display text-6xl md:text-8xl leading-[0.88] tracking-tighter text-stone-100"
              >
                {sauce.name}
              </h1>
              <p className="mt-6 max-w-2xl font-serif-display italic text-xl md:text-2xl text-stone-300 leading-snug">
                {sauce.subtitle}
              </p>
            </div>
            {sauce.image_status === "ready" && (
              <div className="lg:col-span-5" data-testid="sauce-hero-image">
                <div className="aspect-[4/5] w-full bg-stone-900 overflow-hidden">
                  <img
                    src={`${BACKEND}/api/sauces/${sauce.slug}/image`}
                    alt={sauce.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start space-y-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Flame className="w-4 h-4 text-orange-600" />
                <h2 className="eyebrow">The base</h2>
              </div>
              <p className="text-sm text-stone-300 leading-relaxed">{sauce.base}</p>
            </div>

            {sauce.uses?.length > 0 && (
              <div className="border-t border-stone-800 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <ChevronRight className="w-4 h-4 text-orange-600" />
                  <h2 className="eyebrow">Serve with</h2>
                </div>
                <ul className="space-y-2">
                  {sauce.uses.map((u, i) => (
                    <li key={i} className="text-sm text-stone-300 leading-relaxed">— {u}</li>
                  ))}
                </ul>
              </div>
            )}

            {sauce.watch_outs?.length > 0 && (
              <div className="border border-orange-900/40 bg-orange-950/10 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <span className="eyebrow text-orange-500">Watch out</span>
                </div>
                <ul className="space-y-3">
                  {sauce.watch_outs.map((w, i) => (
                    <li key={i} className="text-sm text-stone-300 leading-relaxed">— {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          <div className="lg:col-span-8">
            {sauce.story && (
              <div className="mb-14 border-l-2 border-orange-800/60 pl-6">
                <p className="font-serif-display italic text-xl text-stone-200 leading-relaxed">
                  {sauce.story}
                </p>
              </div>
            )}

            <div className="mb-4 flex items-center gap-3 border-t border-stone-800 pt-6">
              <ChefHat className="w-4 h-4 text-orange-600" />
              <h2 className="eyebrow">Method</h2>
            </div>
            <ol className="space-y-8 mt-8">
              {(sauce.method || []).map((step, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-6" data-testid={`sauce-step-${i}`}>
                  <span className="magazine-number text-3xl text-orange-600 leading-[0.8]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-stone-200 leading-relaxed pt-1">{step}</p>
                </li>
              ))}
            </ol>

            {sauce.chef_tips?.length > 0 && (
              <div className="mt-16 border-t border-stone-800 pt-10">
                <div className="flex items-center gap-3 mb-6">
                  <span className="eyebrow text-lime-500">Chef&apos;s notebook</span>
                </div>
                <ul className="space-y-4">
                  {sauce.chef_tips.map((t, i) => (
                    <li key={i} className="font-serif-display italic text-lg text-stone-200 leading-snug">
                      &ldquo;{t}&rdquo;
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SauceDetail;
